/* Saint Véra: card compartilhado, compra rápida, coleção e ocasião (dono: agente E).
   Módulo único e idempotente. Depende de window.SV (sv-core.js) com fallbacks mínimos.
   Superfícies: delegação global (coração, cor do card, compra rápida) + custom elements
   <sv-collection>, <sv-eta>, <sv-planner>, <sv-deadlines>. */

const doc = document;
const SVx = () => window.SV || {};
const $ = (s, r = doc) => r.querySelector(s);
const $$ = (s, r = doc) => Array.from(r.querySelectorAll(s));
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const root = () => SVx().config?.routes?.root || window.Shopify?.routes?.root || '/';
const pixPct = () => (typeof SVx().config?.pixPct === 'number' ? SVx().config.pixPct : 5);
const money = (c) => (SVx().money ? SVx().money(c) : 'R$ ' + (c / 100).toFixed(2).replace('.', ','));
const pix = (c) => (SVx().pix ? SVx().pix(c) : Math.floor((c * (100 - pixPct()) + 50) / 100));
const icon = (n) =>
  SVx().icon
    ? SVx().icon(n)
    : '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
const emit = (n, d) => (SVx().emit ? SVx().emit(n, d) : doc.dispatchEvent(new CustomEvent('sv:' + n, { detail: d })));
const store = {
  get(k, f) {
    try {
      if (SVx().store) return SVx().store.get(k, f);
      const v = localStorage.getItem('sv:' + k);
      return v == null ? f : JSON.parse(v);
    } catch (e) {
      return f;
    }
  },
  set(k, v) {
    try {
      if (SVx().store) return SVx().store.set(k, v);
      localStorage.setItem('sv:' + k, JSON.stringify(v));
    } catch (e) {}
  },
};
const lock = () => (SVx().lockScroll ? SVx().lockScroll() : void (doc.documentElement.style.overflow = 'hidden'));
const unlock = () => (SVx().unlockScroll ? SVx().unlockScroll() : void (doc.documentElement.style.overflow = ''));
const trap = (el, onEscape) => {
  if (SVx().focusTrap) return SVx().focusTrap(el, { onEscape });
  const prev = doc.activeElement;
  const onKey = (e) => {
    if (e.key === 'Escape') onEscape?.(e);
  };
  el.addEventListener('keydown', onKey);
  el.focus();
  return () => {
    el.removeEventListener('keydown', onKey);
    prev?.focus?.();
  };
};
const imgSized = (src, w) => {
  try {
    const u = new URL(src, location.origin);
    u.searchParams.set('width', w);
    return u.href;
  } catch (e) {
    return src;
  }
};

/* ---------- datas e prazo: fonte única SV.ship (prazo total da compra à entrega, dias úteis) ---------- */
const DAY = 864e5;
const ship = () => SVx().ship || null;
const shipDays = () => (ship()?.days ? ship().days() : SVx().config?.shipping?.days || [5, 12]);
const shipText = () => (ship()?.text ? ship().text() : shipDays()[0] + ' a ' + shipDays()[1] + ' dias úteis');
const addBiz = (d, n) => {
  if (ship()?.addBusinessDays) return ship().addBusinessDays(d, n);
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  while (n > 0) {
    r.setDate(r.getDate() + 1);
    if (r.getDay() !== 0 && r.getDay() !== 6) n--;
  }
  return r;
};
const today = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const win = (from = today()) => (ship()?.window ? ship().window(from) : { start: addBiz(from, shipDays()[0]), end: addBiz(from, shipDays()[1]) });
// último dia para comprar e receber até a data (null se já não dá)
const latestOrder = (target) => {
  if (ship()?.latestOrder) return ship().latestOrder(target);
  let c = new Date(target);
  while (addBiz(c, shipDays()[1]) > target) c = addDays(c, -1);
  return c < today() ? null : c;
};
const p2 = (n) => String(n).padStart(2, '0');
const dm = (d) => p2(d.getDate()) + '/' + p2(d.getMonth() + 1);
const longDay = (d) => d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
const parseDay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const isoDay = (d) => d.getFullYear() + '-' + p2(d.getMonth() + 1) + '-' + p2(d.getDate());
// estimativa honesta: nunca promete além do prazo publicado
const etaStatus = (ev) => {
  const t = today();
  if (ev < t) return { n: 'passou', b: 'Escolha uma data futura.', p: 'Essa data já passou.' };
  const last = latestOrder(ev);
  if (last) {
    const days = Math.round((last - t) / DAY);
    return {
      n: 'tempo',
      b: 'Chega a tempo.',
      p: 'Para receber até ' + longDay(ev) + ', peça até ' + longDay(last) + (days === 0 ? ' (hoje é o último dia).' : '.'),
    };
  }
  const w = win(t);
  return {
    n: 'risco',
    b: 'A data está próxima.',
    p:
      'Pedindo hoje, a entrega fica entre ' + dm(w.start) + ' e ' + dm(w.end) +
      (w.start <= ev ? ', e pode chegar até ' + longDay(ev) + '.' : ', depois de ' + longDay(ev) + '.') +
      ' Fale com a gente antes de pedir.',
  };
};

if (!window.__svCollectionInit) {
  window.__svCollectionInit = true;

  /* ================= coração e cor do card (delegação) ================= */
  const syncFav = (rootEl = doc) => {
    if (!SVx().fav) return;
    const btns = $$('[data-sv-fav]', rootEl);
    if (rootEl.matches?.('[data-sv-fav]')) btns.push(rootEl);
    btns.forEach((b) => {
      const on = SVx().fav.has(b.dataset.handle);
      if (b.getAttribute('aria-pressed') !== String(on)) b.setAttribute('aria-pressed', String(on));
    });
  };
  doc.addEventListener('click', (e) => {
    const fav = e.target.closest('[data-sv-fav]');
    if (fav && SVx().fav) {
      e.preventDefault();
      SVx().fav.toggle({ handle: fav.dataset.handle, variantId: fav.dataset.variantId, title: fav.dataset.title });
      fav.classList.remove('is-pop');
      void fav.offsetWidth;
      fav.classList.add('is-pop');
      syncFav();
      return;
    }
    const dot = e.target.closest('.sv-card__dot');
    if (dot) {
      e.preventDefault();
      const card = dot.closest('[data-sv-card]');
      const name = $('[data-sv-cname]', card);
      if (name && !name.dataset.orig) name.dataset.orig = name.textContent;
      const on = dot.getAttribute('aria-pressed') === 'true';
      $$('.sv-card__dot', card).forEach((d) => d.setAttribute('aria-pressed', 'false'));
      if (!on) dot.setAttribute('aria-pressed', 'true');
      if (name) name.textContent = on ? name.dataset.orig : dot.dataset.value;
      const m = $('.sv-card__m', card);
      const img = $('.sv-card__img:not(.sv-card__img--2)', card);
      if (!dot.dataset.img || !img || !m) return;
      if (!img.dataset.orig) {
        img.dataset.orig = img.currentSrc || img.src;
        img.dataset.origSet = img.getAttribute('srcset') || '';
        img.dataset.origSizes = img.getAttribute('sizes') || '';
      }
      if (on) {
        img.src = img.dataset.orig;
        if (img.dataset.origSet) img.setAttribute('srcset', img.dataset.origSet);
        img.setAttribute('sizes', img.dataset.origSizes);
        m.classList.remove('is-swapped');
      } else {
        img.removeAttribute('srcset');
        img.src = dot.dataset.img;
        m.classList.add('is-swapped');
      }
      return;
    }
    const q = e.target.closest('[data-sv-quick]');
    if (q) {
      e.preventDefault();
      quick.open(q.dataset.svQuick, q);
      return;
    }
    // card da sacola: o dono da sacola pode interceptar (preventDefault) e abrir a mini-PDP; sem isso, compra rápida
    const pk = e.target.closest('[data-sv-pick]');
    if (pk && !e.defaultPrevented) {
      e.preventDefault();
      quick.open(pk.dataset.svPick, pk);
    }
  });
  doc.addEventListener('sv:fav:change', () => syncFav());
  doc.addEventListener('sv:quick:open', (e) => e.detail?.handle && quick.open(e.detail.handle, doc.activeElement));
  // Observa o documento todo (cards entram em gaveta, carrossel, busca), mas só sincroniza os nós que chegaram: nada de varrer a página a cada script/pixel inserido
  let favQueue = [];
  new MutationObserver((muts) => {
    const first = !favQueue.length;
    muts.forEach((m) =>
      m.addedNodes.forEach((n) => {
        if (n.nodeType === 1 && (n.matches('[data-sv-fav]') || n.querySelector('[data-sv-fav]'))) favQueue.push(n);
      })
    );
    if (!first || !favQueue.length) return;
    requestAnimationFrame(() => {
      const q = favQueue;
      favQueue = [];
      q.forEach((n) => n.isConnected && syncFav(n));
    });
  }).observe(doc.documentElement, { childList: true, subtree: true });
  if (doc.readyState !== 'loading') syncFav();
  else doc.addEventListener('DOMContentLoaded', () => syncFav(), { once: true });

  // trilho de chips (celular): o chip da coleção atual fica visível; rola só o trilho, nunca a página
  const chipRail = doc.querySelector('.sv-hero__chips');
  const chipOn = chipRail && chipRail.querySelector('[aria-current="page"]');
  if (chipOn && chipRail.scrollWidth > chipRail.clientWidth) {
    const rail = chipRail.getBoundingClientRect();
    const chip = chipOn.getBoundingClientRect();
    if (chip.right > rail.right) chipRail.scrollLeft += chip.right - rail.right + 16;
    else if (chip.left < rail.left) chipRail.scrollLeft -= rail.left - chip.left + 16;
  }
  doc.addEventListener('sv:ready', () => syncFav());

  /* ================= compra rápida ================= */
  const quick = {
    el: null,
    cache: new Map(),
    s: null,
    release: null,

    build() {
      if (this.el) return;
      const el = doc.createElement('div');
      el.className = 'sv-qk';
      el.innerHTML =
        '<div class="sv-qk__ov" data-qk-close></div>' +
        '<div class="sv-qk__p" role="dialog" aria-modal="true" aria-labelledby="svQkT" tabindex="-1">' +
        '<div class="sv-qk__h"><h2 class="sv-qk__t" id="svQkT">Compra rápida</h2>' +
        '<button type="button" class="sv-qk__x" data-qk-close aria-label="Fechar compra rápida">' + icon('close') + '</button></div>' +
        '<div class="sv-qk__b" data-qk-body></div><div class="sv-qk__f" data-qk-foot></div></div>';
      doc.body.appendChild(el);
      this.el = el;
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-qk-close]')) return this.close();
        const o = e.target.closest('[data-opt]');
        if (o) return this.pick(+o.dataset.opt, o.dataset.val, o);
        const t = e.target.closest('[data-thumb]');
        if (t) {
          this.s.img = t.dataset.thumb;
          this.render();
          return;
        }
        if (e.target.closest('[data-qk-add]')) this.add();
      });
    },

    async open(handle, trigger) {
      this.build();
      this.s = { handle, p: null, sel: [], img: null, err: null, busy: false, dots: this.dotsFrom(trigger), trigger };
      this.el.classList.add('is-open');
      lock();
      const panel = $('.sv-qk__p', this.el);
      this.release = trap(panel, () => this.close());
      $('[data-qk-body]', this.el).innerHTML = '<p class="sv-qk__skel" role="status">Carregando a peça...</p>';
      $('[data-qk-foot]', this.el).innerHTML = '';
      try {
        let p = this.cache.get(handle);
        if (!p) {
          const r = await fetch(root() + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } });
          if (!r.ok) throw new Error('http ' + r.status);
          p = await r.json();
          this.cache.set(handle, p);
        }
        if (!this.s || this.s.handle !== handle) return;
        this.s.p = p;
        this.init();
        this.render();
      } catch (err) {
        $('[data-qk-body]', this.el).innerHTML =
          '<p class="sv-qk__skel" role="alert">Não deu para abrir a peça agora. <a href="' + root() + 'products/' + esc(handle) + '">Ver a página da peça</a>.</p>';
      }
    },

    close() {
      if (!this.el?.classList.contains('is-open')) return;
      this.el.classList.remove('is-open');
      unlock();
      const r = this.release;
      this.release = null;
      r?.();
      this.s = null;
    },

    dotsFrom(trigger) {
      const map = {};
      const card = trigger?.closest?.('[data-sv-card]');
      if (card) $$('.sv-card__dot', card).forEach((d) => (map[d.dataset.value] = d.style.getPropertyValue('--sv-dot')));
      return map;
    },

    opts() {
      const p = this.s.p;
      return (p.options || []).map((o, i) => {
        const name = typeof o === 'string' ? o : o.name;
        const values = (typeof o === 'object' && o.values) || [...new Set(p.variants.map((v) => v.options[i]))];
        return { name, values, color: /^(cor|cores|color|colour)$/i.test(String(name).trim()) };
      });
    },

    init() {
      const { p } = this.s;
      const first = p.variants.find((v) => v.available) || p.variants[0];
      this.s.sel = this.opts().map((o, i) => (o.color ? first.options[i] : o.values.length === 1 ? o.values[0] : null));
      this.s.img = this.colorImg() || p.featured_image;
    },

    variant() {
      const { p, sel } = this.s;
      if (sel.some((v) => v == null)) return null;
      return p.variants.find((v) => v.options.every((x, i) => x === sel[i])) || null;
    },

    colorImg() {
      const { p, sel } = this.s;
      const ci = this.opts().findIndex((o) => o.color);
      const v = p.variants.find((x) => (ci < 0 || x.options[ci] === sel[ci]) && x.featured_image);
      return v?.featured_image?.src || null;
    },

    avail(i, val) {
      const { p, sel } = this.s;
      return p.variants.some((v) => v.available && v.options[i] === val && v.options.every((x, j) => j === i || sel[j] == null || x === sel[j]));
    },

    pick(i, val, btn) {
      const o = this.opts()[i];
      if (btn?.getAttribute('aria-disabled') === 'true') return;
      this.s.sel[i] = this.s.sel[i] === val && !o.color && o.values.length > 1 ? null : val;
      // trocar a cor pode invalidar tamanho já escolhido: limpa em vez de escolher outro em silêncio
      this.opts().forEach((x, j) => {
        if (j !== i && this.s.sel[j] != null && !this.avail(j, this.s.sel[j])) this.s.sel[j] = null;
      });
      if (o.color) this.s.img = this.colorImg() || this.s.img;
      this.s.err = null;
      this.render(`o${i}-${val}`);
    },

    etaLine() {
      const w = win();
      const ev = parseDay(store.get('evento', null)?.data);
      const st = ev ? etaStatus(ev) : null;
      return (
        '<div class="sv-qk__eta">' + icon('truck') + '<p>Pedindo hoje, chega entre <b>' + dm(w.start) + ' e ' + dm(w.end) + '</b>, com frete grátis (' + esc(shipText()) + ').' +
        (st && st.n !== 'passou' ? '<br>' + esc(st.p) : '') + '</p></div>'
      );
    },

    render(focusKey) {
      const s = this.s;
      if (!s?.p) return;
      const { p } = s;
      const opts = this.opts();
      const v = this.variant();
      const ref = v || p.variants.find((x) => x.available) || p.variants[0];
      const ae = doc.activeElement;
      const key = focusKey || ae?.dataset?.k || null;
      const inst = SVx().installment ? SVx().installment(ref.price) : null;
      const head =
        '<div class="sv-qk__hd"><a class="sv-qk__fig" href="' + esc(p.url) + '"><img src="' + esc(imgSized(s.img || p.featured_image, 300)) + '" alt="' + esc(p.title) + '" width="96" height="128"></a>' +
        '<div><a class="sv-qk__name" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
        '<p class="sv-qk__pix">' + money(pix(ref.price)) + ' no Pix <span class="sv-qk__badge">' + pixPct() + '% OFF</span></p>' +
        '<p class="sv-qk__card">' + money(ref.price) + ' no cartão' +
        (ref.compare_at_price > ref.price ? ' <s>' + money(ref.compare_at_price) + '</s>' : '') +
        (inst ? '<br>em até ' + inst.n + 'x' + (inst.interest ? '*' : '') + ' de ' + money(inst.value) : '') + '</p></div></div>';
      const imgs = (p.images || []).slice(0, 8);
      const thumbs =
        imgs.length > 1
          ? '<ul class="sv-qk__thumbs" aria-label="Fotos">' +
            imgs.map((src, i) => '<li><button type="button" class="sv-qk__th" data-thumb="' + esc(src) + '" data-k="t' + i + '" aria-label="Foto ' + (i + 1) + '"' +
              (src === s.img || imgSized(src, 1) === imgSized(s.img || '', 1) ? ' aria-current="true"' : '') + '><img src="' + esc(imgSized(src, 120)) + '" alt="" loading="lazy" width="56" height="72"></button></li>').join('') + '</ul>'
          : '';
      const groups = opts.map((o, i) => {
        const cur = s.sel[i];
        const btns = o.values.map((val) => {
          const ok = this.avail(i, val);
          const pressed = cur === val;
          const label = val + (ok ? '' : ' (indisponível)');
          if (o.color) {
            const cv = p.variants.find((x) => x.options[i] === val && x.featured_image);
            const bg = s.dots[val] || (cv ? 'url(' + imgSized(cv.featured_image.src, 80) + ')' : 'transparent');
            return '<button type="button" class="sv-qk__sw" data-opt="' + i + '" data-val="' + esc(val) + '" data-k="o' + i + '-' + esc(val) + '" aria-pressed="' + pressed + '" aria-disabled="' + !ok + '" aria-label="' + esc(label) + '" title="' + esc(val) + '"><i style="--sv-dot:' + esc(bg) + '"></i></button>';
          }
          return '<button type="button" class="sv-qk__txt" data-opt="' + i + '" data-val="' + esc(val) + '" data-k="o' + i + '-' + esc(val) + '" aria-pressed="' + pressed + '" aria-disabled="' + !ok + '" aria-label="' + esc(label) + '">' + esc(val) + '</button>';
        }).join('');
        return '<div class="sv-qk__g" role="group" aria-labelledby="svQkL' + i + '"><p class="sv-qk__l" id="svQkL' + i + '">' + esc(o.name) + (cur ? ': <b>' + esc(cur) + '</b>' : '') + '</p>' +
          '<div class="sv-qk__opts' + (s.err && cur == null ? ' is-err' : '') + '">' + btns + '</div></div>';
      }).join('');
      $('[data-qk-body]', this.el).innerHTML = head + thumbs + groups + this.etaLine();
      const unavailable = v && !v.available;
      $('[data-qk-foot]', this.el).innerHTML =
        '<p class="sv-qk__err" role="alert"' + (s.err || unavailable ? '' : ' hidden') + '>' + esc(s.err || (unavailable ? 'Esta combinação está esgotada.' : '')) + '</p>' +
        '<button type="button" class="sv-qk__add sv-btn sv-btn--add" data-qk-add data-k="add"' + (s.busy || unavailable ? ' disabled' : '') + '>' + (s.busy ? 'Adicionando...' : 'Adicionar à sacola') + '</button>' +
        '<a class="sv-qk__link" href="' + esc(p.url) + '">Ver todos os detalhes da peça</a>';
      if (key) $('[data-k="' + CSS.escape(key) + '"]', this.el)?.focus({ preventScroll: true });
    },

    async add() {
      const s = this.s;
      if (!s?.p || s.busy) return;
      const v = this.variant();
      if (!v) {
        const i = s.sel.findIndex((x) => x == null);
        const o = this.opts()[i];
        s.err = 'Escolha ' + (o ? (/^(tamanho|size)/i.test(o.name) ? 'o ' : 'a ') + String(o.name).toLowerCase() : 'as opções') + ' para adicionar.';
        this.render();
        $$('.sv-qk__g', this.el)[i]?.querySelector('button')?.focus();
        return;
      }
      if (!v.available) return;
      s.busy = true;
      this.render('add');
      try {
        if (SVx().cart?.add) await SVx().cart.add({ id: v.id, quantity: 1 }, { context: 'quick-add', target: s.trigger || doc });
        else {
          const r = await fetch(root() + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] }) });
          if (!r.ok) throw new Error('add');
        }
        emit('quick:added', { handle: s.p.handle, variantId: v.id });
        this.close();
      } catch (err) {
        s.busy = false;
        s.err = 'Não foi possível adicionar agora. Tente de novo.';
        this.render('add');
      }
    },
  };

  /* ================= <sv-collection>: barra de filtros nativos, densidade, ordenação ================= */
  const desk = matchMedia('(min-width: 1000px)');
  class SvCollection extends HTMLElement {
    connectedCallback() {
      this.sid = this.dataset.sectionId;
      this._ac = new AbortController();
      const o = { signal: this._ac.signal };
      this.applyDensity();
      this.initTabs();
      this.addEventListener('click', (e) => this.onClick(e), o);
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-sv-f], [data-sv-price], [data-sv-sort-select]')) this.submit();
      }, o);
      this.addEventListener('submit', (e) => {
        if (!e.target.matches('[data-sv-form]')) return;
        e.preventDefault();
        this.submit();
      }, o);
      desk.addEventListener?.('change', () => this.applyDensity(), o);
      window.addEventListener('popstate', () => this.go(location.pathname + location.search, false), o);
      // "Carregar mais" sozinho perto do fim da grade (Clarity 20/10/2026: 115 toques nele). Até 3 vezes, para o rodapé
      // continuar alcançável; depois disso fica o botão.
      const wrap = $('[data-sv-more-wrap]', this);
      if (wrap && 'IntersectionObserver' in window) {
        let auto = 0;
        const io = new IntersectionObserver((es) => {
          const b = es.some((x) => x.isIntersecting) && $('[data-sv-more]', wrap);
          if (b && auto < 3) { auto++; this.more(b, true); }
        }, { rootMargin: '600px 0px' });
        io.observe(wrap);
        this._ac.signal.addEventListener('abort', () => io.disconnect());
      }
    }

    disconnectedCallback() {
      this._ac?.abort();
      this._fetch?.abort();
    }

    /* densidade: 2/3/4 no computador, 1/2 no celular (guardadas separadas) */
    applyDensity() {
      const d = Math.min(4, Math.max(2, +store.get('cl:d', +this.dataset.defaultD || 4)));
      const m = Math.min(2, Math.max(1, +store.get('cl:m', +this.dataset.defaultM || 2)));
      const grid = $('[data-sv-grid]', this);
      if (grid) {
        grid.dataset.d = d;
        grid.dataset.m = m;
        this.syncSizes(grid, m);
      }
      const cur = desk.matches ? d : m;
      $$('[data-sv-density]', this).forEach((b) => b.setAttribute('aria-checked', String(+b.dataset.svDensity === cur)));
    }

    /* sizes das fotos acompanha a densidade do celular (o Liquid só conhece o padrão da seção); só reescreve se mudou */
    syncSizes(grid, m = +grid.dataset.m) {
      const s = '(min-width: 1000px) 25vw, ' + (m === 1 ? 'calc(100vw - 40px)' : 'calc(50vw - 27px)');
      $$('.sv-card__img', grid).forEach((i) => {
        if (!i.dataset.orig && i.getAttribute('sizes') !== s) i.setAttribute('sizes', s);
      });
    }

    initTabs() {
      const looks = $('[data-sv-looks]', this);
      const tabs = $('[data-sv-tabs]', this);
      if (tabs) tabs.hidden = !looks;
      this.classList.remove('is-looks');
      $$('[data-sv-view]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.svView === 'pecas')));
    }

    setView(v) {
      const looks = $('[data-sv-looks]', this);
      if (!looks) return;
      const on = v === 'looks';
      this.classList.toggle('is-looks', on);
      looks.hidden = !on;
      $('[data-sv-more-wrap]', this)?.toggleAttribute('hidden', on);
      $$('[data-sv-view]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.svView === v)));
    }

    onClick(e) {
      const t = e.target;
      let b;
      if ((b = t.closest('[data-sv-density]'))) {
        store.set(desk.matches ? 'cl:d' : 'cl:m', +b.dataset.svDensity);
        return this.applyDensity();
      }
      if ((b = t.closest('[data-sv-view]'))) return this.setView(b.dataset.svView);
      if ((b = t.closest('[data-sv-more]'))) {
        if (e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        return this.more(b);
      }
      if ((b = t.closest('a[data-sv-nav]')) && !(e.metaKey || e.ctrlKey || e.shiftKey)) {
        e.preventDefault();
        this.go(b.getAttribute('href'), true);
      }
    }

    /* URL de estado: um valor por filtro (select), preço por faixa, ordenação */
    buildUrl() {
      const p = new URLSearchParams();
      $$('[data-sv-f]', this).forEach((sel) => sel.value && p.append(sel.name, sel.value));
      const pr = $('[data-sv-price]', this);
      if (pr && pr.value) {
        const [gte, lte] = pr.value.split(':');
        if (gte) p.append(pr.dataset.gte, gte);
        if (lte) p.append(pr.dataset.lte, lte);
      }
      const sort = $('[data-sv-sort-select]', this)?.value;
      const dflt = $('[data-sv-sort-input]', this)?.dataset.default;
      if (sort && sort !== dflt) p.append('sort_by', sort);
      const q = p.toString();
      return location.pathname + (q ? '?' + q : '');
    }

    submit() {
      this.go(this.buildUrl(), true);
    }

    async fetchSection(url, signal) {
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', this.sid);
      const r = await fetch(u.href, { signal, headers: { Accept: 'text/html' } });
      if (!r.ok) throw new Error('http ' + r.status);
      const dom = new DOMParser().parseFromString(await r.text(), 'text/html');
      const next = $('sv-collection', dom);
      if (!next) throw new Error('sem seção');
      return next;
    }

    async go(url, push) {
      this._fetch?.abort();
      const ac = (this._fetch = new AbortController());
      this.setAttribute('aria-busy', 'true');
      try {
        const next = await this.fetchSection(url, ac.signal);
        this.swap(next);
        if (push) history.pushState({ sv: 1 }, '', url);
        const n = +next.querySelector('[data-sv-count]')?.dataset.svCount || 0;
        const live = $('[data-sv-live]', this);
        if (live) live.textContent = n + (n === 1 ? ' peça' : ' peças');
      } catch (err) {
        if (err.name === 'AbortError') return;
        location.href = url;
      } finally {
        if (this._fetch === ac) this.removeAttribute('aria-busy');
      }
    }

    swap(next) {
      const ae = doc.activeElement;
      const focusKey = ae && this.contains(ae) ? ae.getAttribute('aria-label') : null;
      for (const sel of ['[data-sv-form]', '[data-sv-active]', '[data-sv-wrap]']) {
        const a = $(sel, this);
        const b = $(sel, next);
        if (a && b) a.innerHTML = b.innerHTML;
      }
      const cnt = $('[data-sv-count]', this);
      const ncnt = $('[data-sv-count]', next);
      if (cnt && ncnt) cnt.dataset.svCount = ncnt.dataset.svCount;
      this.applyDensity();
      this.initTabs();
      if (focusKey) $('[aria-label="' + CSS.escape(focusKey) + '"]', this)?.focus({ preventScroll: true });
      const wrap = $('[data-sv-wrap]', this);
      const top = wrap.getBoundingClientRect().top;
      if (top < 0) $('[data-sv-bar]', this).scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    async more(btn, auto) {
      if (btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'Carregando...';
      try {
        const next = await this.fetchSection(btn.getAttribute('href'));
        const grid = $('[data-sv-grid]', this);
        const items = $$('[data-sv-grid] > li', next);
        const first = items.find((li) => li.matches('[data-sv-item]'));
        items.forEach((li) => {
          const n = doc.importNode(li, true);
          n.classList.add('is-new');
          grid.appendChild(n);
        });
        const nl = $('[data-sv-looks]', next);
        if (nl) {
          let looks = $('[data-sv-looks]', this);
          if (!looks) {
            looks = doc.createElement('ul');
            looks.className = 'sv-cl__looks';
            looks.hidden = true;
            looks.setAttribute('data-sv-looks', '');
            grid.after(looks);
          }
          $$('li', nl).forEach((li) => looks.appendChild(doc.importNode(li, true)));
          const tabs = $('[data-sv-tabs]', this);
          if (tabs) tabs.hidden = false;
        }
        this.syncSizes(grid);
        $('[data-sv-more-wrap]', this).innerHTML = $('[data-sv-more-wrap]', next).innerHTML;
        const live = $('[data-sv-live]', this);
        if (live) live.textContent = items.filter((li) => li.matches('[data-sv-item]')).length + ' peças a mais carregadas';
        if (first && !auto) grid.querySelector('[data-sv-item].is-new .sv-card__name a')?.focus({ preventScroll: true });
      } catch (err) {
        if (auto) { btn.removeAttribute('aria-busy'); btn.textContent = 'Carregar mais'; return; }
        location.href = btn.getAttribute('href');
      }
    }
  }

  /* ================= <sv-eta> (linha de prazo da coleção, SV.ship) ================= */
  class SvEta extends HTMLElement {
    connectedCallback() {
      this.render();
      this._h = () => this.render();
      doc.addEventListener('sv:evento', this._h);
      doc.addEventListener('sv:ready', this._h);
    }
    disconnectedCallback() {
      doc.removeEventListener('sv:evento', this._h);
      doc.removeEventListener('sv:ready', this._h);
    }
    render() {
      const w = win();
      const ev = parseDay(store.get('evento', null)?.data);
      const st = ev ? etaStatus(ev) : null;
      this.innerHTML =
        '<span class="sv-eta__i">' + icon('truck') + '</span><p class="sv-eta__t">Frete grátis. Pedindo hoje, chega entre <b>' + dm(w.start) + ' e ' + dm(w.end) + '</b> (' + esc(shipText()) + ').' +
        (st && st.n !== 'passou' ? ' <span class="sv-eta__ev">' + esc(st.n === 'tempo' ? st.p : 'Sua data, ' + longDay(ev) + ', fica antes do fim do prazo. Fale com a gente antes de pedir.') + '</span>' : '') + '</p>' +
        '<label class="sv-eta__date">' + icon('calendar') + '<span>' + (ev ? 'Mudar a data' : 'Tem data marcada?') + '</span>' +
        '<input type="date" min="' + isoDay(today()) + '" value="' + (ev ? isoDay(ev) : '') + '" aria-label="Data do seu compromisso"></label>';
      // toque no texto do prazo abre o calendário (Clarity 20/10/2026: cliques mortos em "Frete grátis…")
      const t = $('.sv-eta__t', this), inp = $('input', this);
      t.style.cursor = 'pointer';
      t.addEventListener('click', () => { try { inp.showPicker(); } catch (e) { inp.focus(); } });
      $('input', this).addEventListener('change', (x) => {
        const v = x.target.value;
        if (v) store.set('evento', { data: v, nome: store.get('evento', null)?.nome || '' });
        else store.set('evento', null);
        emit('evento', { data: v });
      });
    }
  }

  /* ================= ocasião: planner e tabela de datas-limite ================= */
  class SvPlanner extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.elements.date.min = isoDay(today());
      const ev = store.get('evento', null);
      if (ev?.data) {
        this.form.elements.date.value = ev.data;
        this.form.elements.name.value = ev.nome || '';
        this.show();
      }
      this.form.addEventListener('submit', (this._s = (e) => {
        e.preventDefault();
        const d = parseDay(this.form.elements.date.value);
        if (!d) {
          this.out({ n: 'erro', b: 'Escolha a data.', p: 'Assim calculamos até quando pedir.' });
          this.form.elements.date.focus();
          return;
        }
        store.set('evento', { data: isoDay(d), nome: this.form.elements.name.value.trim() });
        emit('evento', { data: isoDay(d) });
        this.show();
      }));
    }
    disconnectedCallback() {
      this.form?.removeEventListener('submit', this._s);
    }
    out(st, name) {
      const o = $('[data-out]', this);
      if (!o) return;
      o.hidden = false;
      o.dataset.n = st.n;
      o.innerHTML =
        icon(st.n === 'tempo' ? 'check' : 'calendar') +
        '<div><b>' + esc(st.b) + '</b><p>' + (name ? esc(name) + ': ' : '') + esc(st.p) + '</p>' +
        '<small>Prazo total de ' + esc(shipText()) + ', da compra à entrega, com frete grátis.</small></div>';
    }
    show() {
      const ev = store.get('evento', null);
      const d = parseDay(ev?.data);
      if (d) this.out(etaStatus(d), ev.nome);
    }
  }

  class SvDeadlines extends HTMLElement {
    connectedCallback() {
      this.render();
      this._h = () => this.render();
      doc.addEventListener('sv:ready', this._h);
    }
    disconnectedCallback() {
      doc.removeEventListener('sv:ready', this._h);
    }
    render() {
      const t = today();
      const rows = +this.dataset.rows || 9;
      const fmt = { weekday: 'long', day: 'numeric', month: 'long' };
      // primeiro sábado em que um pedido de hoje ainda chega no fim do prazo
      let d = t;
      while (d.getDay() !== 6 || !latestOrder(d)) d = addDays(d, 1);
      const list = [];
      for (let i = 0; i < rows; i++) {
        list.push(d);
        d = addDays(d, i < rows - 2 ? 7 : 14);
      }
      this.innerHTML =
        '<table class="sv-oc-dl"><thead><tr><th scope="col">' + esc(this.dataset.col1 || 'Se o evento é em') + '</th><th scope="col">Peça até</th></tr></thead><tbody>' +
        list.map((x) => '<tr><td>' + esc(x.toLocaleDateString('pt-BR', x.getFullYear() !== t.getFullYear() ? { ...fmt, year: 'numeric' } : fmt)) + '</td><td>' + dm(latestOrder(x)) + '</td></tr>').join('') +
        '</tbody></table>';
    }
  }

  // toque na foto do topo da coleção = botão da foto (Clarity 20/10/2026: cliques mortos em "Luana na campanha")
  doc.addEventListener('click', (e) => {
    const m = e.target.closest && e.target.closest('.sv-hero__m');
    if (!m || e.target.closest('a,button')) return;
    m.querySelector('.sv-hero__cta')?.click();
  });
  doc.querySelectorAll('.sv-hero__m').forEach((m) => { if (m.querySelector('.sv-hero__cta')) m.style.cursor = 'pointer'; });

  const define = (n, c) => !customElements.get(n) && customElements.define(n, c);
  define('sv-collection', SvCollection);
  define('sv-eta', SvEta);
  define('sv-planner', SvPlanner);
  define('sv-deadlines', SvDeadlines);
}
